import { AlertTriangle, Rocket } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useSetupWizard } from "@/contexts/SetupWizardContext";

/**
 * Tarjeta del Panel que abre el asistente guiado (27-sep-2026).
 *
 * Hay dos tonos, y la diferencia importa:
 *  - Si el negocio NO PUEDE recibir citas todavía (le falta horario, servicios o
 *    un profesional con horario), el aviso es ROJO y lo dice sin rodeos: su
 *    enlace no funciona. Es el caso que nos estaba costando clientes.
 *  - Si ya puede pero le queda algo por montar (el mapa de mesas, las salas), es
 *    una sugerencia tranquila. Aquí no se asusta a nadie: sin mapa de mesas se
 *    reserva igual, por aforo.
 *
 * La lista de pasos que faltan y la ventana viven en `components/setup-wizard/`;
 * esto es solo la puerta de entrada.
 */

const NOMBRES: Record<string, string> = {
  horario: "el horario",
  servicios: "los servicios",
  profesionales: "los profesionales",
  salas: "las salas",
  mesas: "las mesas",
};

export default function SetupWizardCard() {
  const { user } = useAuth();
  const { estado, abrir, pendiente } = useSetupWizard();
  const esAdmin = user?.role === "admin" || user?.role === "owner";

  // `pendiente` ya descuenta lo opcional (las salas) y respeta el "no volver a
  // enseñarlo": ver `SetupWizardContext`.
  if (!esAdmin || !estado || !pendiente) return null;

  const bloqueado = !estado.puedeRecibir;
  const faltan = estado.pasos
    .filter((p) => p.nivel !== "opcional" && !p.hecho)
    .map((p) => NOMBRES[p.id] ?? p.id);

  return (
    <div
      className={`rounded-xl border p-4 md:p-5 ${
        bloqueado
          ? "bg-amber-50 border-amber-300"
          : "bg-white border-gray-200 shadow-sm"
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
            bloqueado
              ? "bg-amber-100 text-amber-700"
              : "bg-primary-100 text-primary-700"
          }`}
        >
          {bloqueado ? (
            <AlertTriangle className="w-5 h-5" />
          ) : (
            <Rocket className="w-5 h-5" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-gray-900">
            {bloqueado
              ? "Tus clientes todavía no pueden pedir cita"
              : "Termina de poner tu negocio en marcha"}
          </p>
          <p className="text-sm text-gray-600 mt-0.5">
            {bloqueado ? (
              <>
                Te falta <strong>{faltan.join(" y ")}</strong>. Te lo pregunto
                paso a paso y lo dejamos funcionando en un par de minutos.
              </>
            ) : (
              <>
                Te queda por configurar <strong>{faltan.join(" y ")}</strong>. Te
                guío paso a paso, sin salir de esta pantalla.
              </>
            )}
          </p>
          <button
            type="button"
            onClick={() => abrir()}
            className={`mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors ${
              bloqueado
                ? "bg-amber-600 hover:bg-amber-700"
                : "bg-primary-600 hover:bg-primary-700"
            }`}
          >
            <Rocket className="w-4 h-4" />
            Configurarlo ahora
          </button>
        </div>
      </div>
    </div>
  );
}
